import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
} from "@material-tailwind/react";
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRFQCustomerInfo, sendMaterialSubmittal, resetStatus } from '@/store/slices/rfqSlice';
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import FilePreview from '@/components/FilePreview';
import Toaster from "@/components/Toaster";
import ShowLoader from '@/components/Loader';

const schema = yup.object({
    material_files: yup
        .mixed()
        .required("At least one PDF file is required")
        .test("fileType", "Only PDF files allowed", (value) => {
            if (!value || value.length === 0) return false;

            // Check every file for PDF type
            return Array.from(value).every(
                (file) => file.type === "application/pdf"
            );
        }),
});


export function MaterialSubmittalModal({ isOpen, onClose, rfq_id }) {
    const dispatch = useDispatch();
    const [isError, setIsError] = useState("");

    const { customersInfo, loading } = useSelector((state) => state.rfq);

    const { register, handleSubmit, setValue, watch, formState: { errors }, reset } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            rfq_id: rfq_id,
            customer_id: customersInfo?.customer?.id || null,
            material_files: []
        }
    });

    useEffect(() => {
        if (isOpen) {
            dispatch(fetchRFQCustomerInfo(rfq_id));
        }
    }, [isOpen]);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);

        const existing = watch("material_files") || [];

        setValue("material_files", [...existing, ...files], { shouldValidate: true });

    };

    const removeFile = (index) => {
        const updated = [...material_files];
        updated.splice(index, 1);
        setValue("material_files", updated, { shouldValidate: true });
    }

    const material_files = watch('material_files');

    const onSubmit = async (data) => {
        try {
            const result = await dispatch(sendMaterialSubmittal(data)).unwrap();
            if (result.status) {
                Toaster.success(`Material Submittal uploaded successfully!`);
                dispatch(resetStatus());
                reset();
                onClose();
            } else {
                // Toaster.error(result.message || "Something went wrong!");
                setIsError(result?.message || "Something went wrong!");
                setTimeout(() => {
                    setIsError('');
                }, 8000);
                dispatch(resetStatus());
            }
        } catch (err) {
            // Toaster.error(err?.message || "Failed to send material submittal");
            setIsError(err?.message || "Something went wrong!");
            setTimeout(() => {
                setIsError('');
            }, 8000);
            dispatch(resetStatus());
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} handler={onClose} size="md" className="rounded-xl">
            {loading && <ShowLoader />}

            {/* Header */}
            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-xl font-bold text-gray-900">Material Submittal</h2>
                {/* <button
                    onClick={onClose}
                    className="p-1 rounded-full text-gray-500 hover:bg-gray-200"
                >
                    <XMarkIcon className="w-6 h-6" />
                </button> */}
            </DialogHeader>

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)}>

                <DialogBody className="px-6 space-y-6 max-h-[65vh] overflow-y-auto">
                    {isError &&
                        <div class="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
                            <span class="font-medium">Oops!</span> {isError}
                        </div>
                    }
                    {/* Customer Info */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-2">This material submittal will be sent to:</p>

                        <p className="text-base font-semibold text-gray-900">
                            {customersInfo?.customer?.first_name} {customersInfo?.customer?.last_name}
                        </p>

                        {customersInfo?.customer?.email && (
                            <p className="text-sm text-gray-700">
                                <span className="font-medium">Email:</span> {customersInfo?.customer.email}
                            </p>
                        )}
                    </div>

                    {/* File Upload */}
                    <div>
                        <label className="block text-sm font-medium mb-2">
                            Upload Material File <span className="text-red-600">*</span>
                        </label>
                        <div
                            onClick={() => document.getElementById("pdf-input").click()}
                            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-yellow-500 transition"
                        >
                            <p className="text-gray-700 font-medium">Click to upload PDF</p>
                            <p className="text-xs text-gray-500 mt-1">Only PDF format</p>

                            <input
                                type="file"
                                id="pdf-input"
                                accept="application/pdf"
                                className="hidden"
                                multiple
                                {...register('material_files')}
                                onChange={handleFileChange}
                            />

                        </div>

                        {errors.material_files && (
                            <p className="text-red-600 text-sm mt-1">{errors.material_files.message}</p>
                        )}

                        {material_files &&
                            material_files?.map((file, index) => {
                                return (<FilePreview
                                    file={file}
                                    onRemove={() => removeFile(index)}
                                    isView={false}
                                    containerClass="relative w-58 h-40 mt-2 rounded-md overflow-hidden border border-gray-300"
                                />)
                            })
                        }
                    </div>

                </DialogBody>
                <DialogFooter className="flex justify-end gap-3 px-6 py-4 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-md font-semibold hover:bg-gray-300"
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        className="px-5 py-2.5 bg-yellow-500 text-white rounded-md font-semibold hover:bg-yellow-600"
                        disabled={loading}
                    >
                        Send to Customer
                    </button>
                </DialogFooter>
            </form>
        </Dialog>
    );
}

export default MaterialSubmittalModal;
